/**
 * Leave Balance Edit Page - Edit user leave balances (Admin/HR only)
 */

import { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import adminApi from '@/modules/admin/api/adminApi'
import LoadingPage from '@/shared/components/LoadingPage'
import type { LeaveBalance } from '@/modules/admin/types/admin.types'

const LeaveBalanceEditPage = () => {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [selectedLeaveType, setSelectedLeaveType] = useState<string>('')
  const [newAllocation, setNewAllocation] = useState<string>('')
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; balance: LeaveBalance | null }>({
    isOpen: false,
    balance: null,
  })

  // Fetch user with balances
  const { data: userData, isLoading: userLoading } = useQuery({
    queryKey: ['adminUser', userId],
    queryFn: () => adminApi.getUser(userId!),
    enabled: !!userId,
  })

  // Fetch leave types
  const { data: leaveTypesData, isLoading: leaveTypesLoading } = useQuery({
    queryKey: ['adminLeaveTypes'],
    queryFn: adminApi.getLeaveTypes,
  })

  const leaveTypes = leaveTypesData?.leave_types || []
  const user = userData?.user

  // Update balance mutation
  const updateBalanceMutation = useMutation({
    mutationFn: async ({ leaveTypeId, totalAllocated }: { leaveTypeId: string; totalAllocated: number }) => {
      const result = await adminApi.updateLeaveBalance(userId!, { leave_type_id: leaveTypeId, total_allocated: totalAllocated })
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] })
      queryClient.invalidateQueries({ queryKey: ['adminUser', userId] })
      toast.success('Leave balance updated successfully')
      setSelectedLeaveType('')
      setNewAllocation('')
    },
    onError: (error: any) => {
      toast.error(error?.error?.message || 'Failed to update leave balance')
    },
  })

  // Delete balance mutation
  const deleteBalanceMutation = useMutation({
    mutationFn: async (leaveTypeId: string) => {
      return await adminApi.deleteLeaveBalance(userId!, leaveTypeId)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] })
      queryClient.invalidateQueries({ queryKey: ['adminUser', userId] })
      toast.success(data.message || 'Leave balance deleted successfully')
      setDeleteModal({ isOpen: false, balance: null })
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message || error?.message || 'Failed to delete leave balance')
    },
  })

  const handleUpdateBalance = () => {
    if (!selectedLeaveType || newAllocation === '') {
      toast.error('Please select a leave type and enter allocation')
      return
    }

    const allocation = parseFloat(newAllocation)
    if (isNaN(allocation) || allocation < 0) {
      toast.error('Please enter a valid non-negative number')
      return
    }

    updateBalanceMutation.mutate({
      leaveTypeId: selectedLeaveType,
      totalAllocated: allocation,
    })
  }

  const getLeaveTypeColor = (leaveTypeName: string) => {
    const leaveType = leaveTypes.find((lt) => lt.name === leaveTypeName)
    return leaveType?.color || '#6b7280'
  }

  const getLeaveTypeById = (id: string) => {
    return leaveTypes.find((lt) => lt.id === id)
  }

  const getInitials = (name: string | undefined) => {
    if (!name) return ''
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  if (userLoading || leaveTypesLoading) return <LoadingPage />

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">User not found</h3>
          <p className="text-gray-500 mb-6">The user you're looking for doesn't exist or has been removed.</p>
          <Link
            to="/leave/manage"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Management
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-red-600 via-red-700 to-rose-700 px-6 py-8 shadow-lg">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 text-red-200 text-sm mb-4">
            <Link to="/leave" className="hover:text-white transition-colors">Leave</Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <Link to="/leave/manage" className="hover:text-white transition-colors">All Employees Balances</Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-white">Edit Balance</span>
          </div>
          <div className="flex items-center gap-5">
            <div className={`w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center text-white font-bold text-xl shadow-lg`}>
              {getInitials(user.full_name)}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white mb-1">{user.full_name}</h1>
              <p className="text-red-200">{user.email}</p>
              {user.department && (
                <span className="inline-flex items-center mt-2 px-2.5 py-1 text-xs font-medium bg-white/20 text-white rounded-lg">
                  {user.department}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Add/Update Balance Form */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-6">
            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add or Update Balance
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Leave Type</label>
              <select
                value={selectedLeaveType}
                onChange={(e) => {
                  setSelectedLeaveType(e.target.value)
                  const existing = user.leave_balances.find(
                    (b) => b.leave_type_id === e.target.value
                  )
                  setNewAllocation(existing ? existing.total_allocated.toString() : '')
                }}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all"
              >
                <option value="">Select leave type...</option>
                {leaveTypes.map((lt) => (
                  <option key={lt.id} value={lt.id}>
                    {lt.name} ({lt.default_days_per_year}d default)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Total Allocated Days</label>
              <input
                type="number"
                min="0"
                step="0.5"
                value={newAllocation}
                onChange={(e) => setNewAllocation(e.target.value)}
                placeholder="e.g., 20"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent focus:bg-white transition-all"
              />
            </div>
          </div>
          {selectedLeaveType && (
            <div className="mt-4 p-4 bg-red-50 rounded-xl border border-red-100">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-red-900">
                    Default allocation for {getLeaveTypeById(selectedLeaveType)?.name}
                  </p>
                  <p className="text-sm text-red-700">
                    {getLeaveTypeById(selectedLeaveType)?.default_days_per_year} days per year
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleUpdateBalance}
              disabled={updateBalanceMutation.isPending || !selectedLeaveType || newAllocation === ''}
              className="px-6 py-3 bg-gradient-to-r from-red-600 to-rose-600 text-white font-medium rounded-xl hover:from-red-700 hover:to-rose-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 shadow-lg shadow-red-500/30"
            >
              {updateBalanceMutation.isPending ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Update Balance
                </>
              )}
            </button>
          </div>
        </div>

        {/* Current Balances */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-6">
            <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Current Leave Balances
          </h2>
          {user.leave_balances.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {user.leave_balances.map((balance) => {
                const color = getLeaveTypeColor(balance.leave_type_name)
                const isUnlimited = balance.total_allocated === 0
                const usagePercent = !isUnlimited
                  ? Math.round((balance.used / balance.total_allocated) * 100)
                  : 0
                return (
                  <div
                    key={balance.leave_type_id}
                    onClick={() => {
                      setSelectedLeaveType(balance.leave_type_id)
                      setNewAllocation(balance.total_allocated.toString())
                    }}
                    className="p-5 rounded-xl border-2 transition-all hover:shadow-md cursor-pointer"
                    style={{
                      borderColor: selectedLeaveType === balance.leave_type_id ? color : `${color}30`,
                      backgroundColor: selectedLeaveType === balance.leave_type_id ? `${color}15` : `${color}08`,
                    }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: color }}
                        />
                        <span className="font-semibold text-gray-900">{balance.leave_type_name}</span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setDeleteModal({ isOpen: true, balance })
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete balance"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                    {isUnlimited ? (
                      <div className="mb-3" />
                    ) : (
                      <>
                        <div className="flex items-baseline gap-2 mb-3">
                          <span className="text-3xl font-bold" style={{ color }}>{balance.available}</span>
                          <span className="text-sm text-gray-500">/ {balance.total_allocated} available</span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-3">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${usagePercent}%`, backgroundColor: color }}
                          />
                        </div>
                      </>
                    )}
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>{balance.used} used</span>
                      {balance.pending > 0 && (
                        <span className="text-amber-600 font-medium">{balance.pending} pending</span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="p-8 bg-gray-50 rounded-xl text-center">
              <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-gray-500 font-medium">No balances configured yet</p>
              <p className="text-sm text-gray-400 mt-1">Use the form above to add a balance</p>
            </div>
          )}
        </div>

        {/* Back Button */}
        <div className="flex justify-start">
          <button
            onClick={() => navigate('/leave/manage')}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-gray-600 font-medium hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to All Employees
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && deleteModal.balance && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setDeleteModal({ isOpen: false, balance: null })}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Leave Balance</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div
                className="p-4 rounded-xl mb-4"
                style={{
                  backgroundColor: `${getLeaveTypeColor(deleteModal.balance.leave_type_name)}10`,
                  borderColor: `${getLeaveTypeColor(deleteModal.balance.leave_type_name)}30`,
                  borderWidth: '2px',
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: getLeaveTypeColor(deleteModal.balance.leave_type_name) }}
                  />
                  <div>
                    <p className="font-semibold text-gray-900">{deleteModal.balance.leave_type_name}</p>
                    <p className="text-sm text-gray-500">
                      {deleteModal.balance.total_allocated} days allocated
                    </p>
                  </div>
                </div>
              </div>

              {(deleteModal.balance.used > 0 || deleteModal.balance.pending > 0) && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                  <div className="flex gap-3">
                    <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-amber-800">Cannot delete this balance</p>
                      <p className="text-sm text-amber-700 mt-1">
                        This balance has {deleteModal.balance.used} used days
                        {deleteModal.balance.pending > 0 && ` and ${deleteModal.balance.pending} pending days`}.
                        Cancel pending requests first.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-gray-600 mb-6">
                Are you sure you want to remove the <strong>{deleteModal.balance.leave_type_name}</strong> balance for this user?
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteModal({ isOpen: false, balance: null })}
                  className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteBalanceMutation.mutate(deleteModal.balance!.leave_type_id)}
                  disabled={deleteBalanceMutation.isPending || deleteModal.balance.used > 0 || deleteModal.balance.pending > 0}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {deleteBalanceMutation.isPending ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    'Delete Balance'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LeaveBalanceEditPage
